// getUserOrgsData function for fetching customer data from Etax
import api from '../../../shared/utils/api';
import { API_BASE_URL } from '../../../shared/utils/api';
import { normalizeCyrillicText, extractNumericPart } from './utils';
import { autoPopulateTaxLogin } from './customerWorkflow';
import { getEtaxToken } from './taxInsuranceLogin';
import { getNovaQApiToken } from '../../../config/novaqToken';

// Get auth headers function
const getAuthHeaders = () => {
  const novaqToken = getNovaQApiToken();
  return novaqToken ? { 'Authorization': `Bearer ${novaqToken}` } : {};
};

// getUserOrgs proxy function
export const getUserOrgs = async () => {
  try {
    const novaqToken = getNovaQApiToken();
    console.log('🔍 getUserOrgs - Token check:', novaqToken ? 'Present' : 'Missing');
    console.log('🔍 getUserOrgs - Token length:', novaqToken ? novaqToken.length : 0);
    console.log('🔍 getUserOrgs - API_BASE_URL:', API_BASE_URL);
    console.log('🔍 getUserOrgs - localStorage keys:', Object.keys(localStorage));
    
    if (!novaqToken) {
      console.error('❌ getUserOrgs - No token found in localStorage');
      console.error('❌ getUserOrgs - Available localStorage items:', Object.keys(localStorage));
      return {
        success: false,
        data: [],
        message: 'Token not found. Please login again.'
      };
    }
    localStorage.getItem('novaqToken');
    console.log('🔍 getUserOrgs - Token:', novaqToken);
    
    // GET method ашиглан туршиж үзье (POST-оос GET болгох)
    const response = await fetch(`${API_BASE_URL}/api/user-orgs-proxy`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${novaqToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('🔍 getUserOrgs - Response status:', response.status);
    console.log('🔍 getUserOrgs - Response statusText:', response.statusText);
    
    if (!response.ok) {
      console.error('❌ getUserOrgs - API call failed:', response.status, response.statusText);
      return {
        success: false,
        data: [],
        message: `API call failed: ${response.status} ${response.statusText}`
      };
    }
    
    const result = await response.json();
    console.log('🔍 getUserOrgs response:', result);
    console.log('🔍 getUserOrgs response type:', typeof result);
    console.log('🔍 getUserOrgs response.data:', result.data);
    console.log('🔍 getUserOrgs response.data type:', typeof result.data);
    console.log('🔍 getUserOrgs response.data isArray:', Array.isArray(result.data));
    
    // Response-г шалгах - data талбар байгаа эсэх, array эсэх
    if (result.success) {
      // Хэрэв data талбар байгаа бол
      if (result.data) {
        // Хэрэв data нь array бол
        if (Array.isArray(result.data)) {
          console.log('✅ getUserOrgsData амжилттай (array)');
          console.log('🔍 Organizations count:', result.data.length);
          // Зөвхөн хэрэгтэй талбаруудыг console-д хэвлэх
          const simplifiedData = result.data.map((org: any) => ({
            id: org.id || org.taxpayerBranchView?.entId || org.entId,
            tin: org.tin || org.taxpayerBranchView?.tin,
            entityName: org.entityName || org.taxpayerBranchView?.legalName || org.legalName,
            pin: org.pin || org.taxpayerBranchView?.pin
          }));
          console.log('🔍 Organizations (simplified):', simplifiedData);
          return {
            success: true,
            data: result.data
          };
        } 
        // Хэрэв data нь object бол array болгох оролдлого
        else if (typeof result.data === 'object') {
          console.log('⚠️ getUserOrgsData data нь object байна, array болгох оролдлого...');
          const dataArray = Array.isArray(result.data.data) ? result.data.data : [result.data];
          console.log('✅ getUserOrgsData амжилттай (converted to array):', dataArray);
          return {
            success: true,
            data: dataArray
          };
        }
      }
      
      // Хэрэв success: true гэхдээ data байхгүй бол
      console.log('⚠️ getUserOrgsData success: true гэхдээ data талбар байхгүй эсвэл array биш');
      console.log('🔍 Full result:', JSON.stringify(result, null, 2));
      return {
        success: false,
        data: [],
        message: 'Response data талбар байхгүй эсвэл array биш',
        details: result
      };
    } else {
      console.log('❌ getUserOrgsData амжилтгүй:', result);
      return {
        success: false,
        data: [],
        message: result.message || 'Мэдээлэл олдсонгүй',
        details: result
      };
    }
  } catch (error) {
    console.error('❌ getUserOrgsData алдаа:', error);
    return {
      success: false,
      data: [],
      message: 'Сүлжээний алдаа'
    };
  }
};

// Get user organizations data from Etax
export const getUserOrgsData = async (
  username: string,
  customerPin: string | undefined,
  setForm: any,
  setErrorMessage: any,
  setShowErrorPopup: any,
  allTaxLogins: any[],
  setAllTaxLogins: any,
  setCustomerNotFoundMessage: any
) => {
  try {
    console.log('📋 Etax service-ээс getUserOrgsData авах...');
    console.log('🔍 Username:', username);
    console.log('🔍 CustomerPin:', customerPin);
    
    // Token шалгах
    const novaqToken = getNovaQApiToken();
    console.log('🔍 getUserOrgsData - Token check:', novaqToken ? 'Present' : 'Missing');
    console.log('🔍 getUserOrgsData - Token length:', novaqToken ? novaqToken.length : 0);
    console.log('🔍 getUserOrgsData - localStorage keys:', Object.keys(localStorage));
    
    if (!novaqToken) {
      console.error('❌ getUserOrgsData - No token found in localStorage');
      setErrorMessage('Token олдсонгүй. Дахин нэвтэрнэ үү.');
      setShowErrorPopup(true);
      return { success: false, message: 'Token олдсонгүй' };
    }
    
    // Check if this is an individual taxpayer (PIN starts with Cyrillic letters)
    const isIndividualTaxpayer = customerPin && /^[А-ЯЁ]/.test(customerPin);
    console.log('🔍 Хувь хүн эсэхийг шалгаж байна (getUserOrgsData):', {
      customerPin,
      isIndividualTaxpayer
    });
    
    // Customers table-аас TaxLoginOid аваад TaxLogin table-аас TaxUsername, Password авах
    let taxUsername: string | null = null;
    let taxPassword: string | null = null;
    
    if (customerPin) {
      // CustomerPin байвал Customers table-аас TaxLoginOid авах
      console.log('🔍 CustomerPin байна, Customers table-аас TaxLoginOid авах...');
      console.log('🔍 CustomerPin:', customerPin);
      
      try {
        // Customer-ийн мэдээлэл авах (TaxLoginOid-тэй)
        const customerResponse = await api.getCustomerByPin(customerPin);
        console.log('🔍 Customer response:', customerResponse);
        
        if (customerResponse.success && customerResponse.data && customerResponse.data.taxLoginOid) {
          const taxLoginOid = customerResponse.data.taxLoginOid;
          console.log('✅ Customer-ийн TaxLoginOid олдлоо:', taxLoginOid);
          
          // TaxLoginOid-оор TaxLogin table-аас TaxUsername, Password авах
          console.log('🔍 TaxLoginOid-оор TaxLogin table-аас мэдээлэл авах...');
          const taxLoginResponse = await api.getTaxLoginByOid(taxLoginOid);
          console.log('🔍 TaxLogin response:', taxLoginResponse);
          
          if (taxLoginResponse.success && taxLoginResponse.data) {
            taxUsername = taxLoginResponse.data.TaxUsername;
            taxPassword = taxLoginResponse.data.Password;
            console.log('✅ TaxLogin мэдээлэл амжилттай авлаа');
            console.log('🔍 TaxUsername:', taxUsername);
            console.log('🔍 Password:', taxPassword ? '***' : 'NO PASSWORD');
          } else {
            console.error('❌ TaxLogin мэдээлэл олдсонгүй TaxLoginOid-оор');
          }
        } else {
          console.error('❌ Customer-ийн TaxLoginOid олдсонгүй');
        }
      } catch (error) {
        console.error('❌ Customer эсвэл TaxLogin мэдээлэл авахад алдаа:', error);
      }
    }
    
    // Хэрэв customerPin-ээр олдохгүй бол fallback: AccountantOid-оор авах
    if (!taxUsername || !taxPassword) {
      console.log('⚠️ CustomerPin-ээр олдсонгүй, AccountantOid-оор fallback хийж байна...');
      console.log('🔍 localStorage-аас accountantOid авах...');
      console.log('🔍 localStorage бүх key-үүд:', Object.keys(localStorage));
      
      let currentAccountantOid = localStorage.getItem('accountantOid');
      console.log('🔍 currentAccountantOid:', currentAccountantOid);
      
      if (!currentAccountantOid) {
        console.error('❌ AccountantOid олдсонгүй localStorage-д');
        setErrorMessage('Татварын нэвтрэх эрх олдсонгүй. Customer эсвэл AccountantOid шаардлагатай.');
        setShowErrorPopup(true);
        return { success: false, message: 'Татварын нэвтрэх эрх олдсонгүй' };
      }
      
      // getTaxLogins-аас username, password авах (fallback)
      console.log('🔍 getTaxLogins-аас username, password авах (fallback)...');
      const taxLoginsResponse = await api.getTaxLogins();
      console.log('🔍 taxLoginsResponse:', taxLoginsResponse);
      
      if (!taxLoginsResponse.success || !taxLoginsResponse.data || taxLoginsResponse.data.length === 0) {
        console.error('❌ Tax logins олдсонгүй');
        setErrorMessage('Татварын нэвтрэх эрх олдсонгүй. Эхлээд татварын нэвтрэх эрх нэмнэ үү.');
        setShowErrorPopup(true);
        return { success: false, message: 'Татварын нэвтрэх эрх олдсонгүй' };
      }
      
      // Эхний tax login-ийг ашиглах
      const firstTaxLogin = taxLoginsResponse.data[0];
      taxUsername = firstTaxLogin.TaxUsername;
      taxPassword = firstTaxLogin.Password;
    }
    
    console.log('🔍 Tax credentials:', {
      username: taxUsername,
      password: taxPassword ? '***' : 'NO PASSWORD'
    });
    
    if (!taxUsername || !taxPassword) {
      console.error('❌ Tax username эсвэл password байхгүй');
      setErrorMessage('Татварын нэвтрэх эрх дутуу байна. Username эсвэл password байхгүй.');
      setShowErrorPopup(true);
      return { success: false, message: 'Татварын нэвтрэх эрх дутуу байна' };
    }
    
    // Etax token авах
    console.log('🔍 Etax token авах...');
    const tokenResult = await fetch(`${API_BASE_URL}/api/etax-proxy/getToken`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: taxUsername,
        password: taxPassword
      })
    });
    
    const tokenData = await tokenResult.json();
    console.log('🔍 Token result:', tokenData);
    
    if (!tokenData.success || !tokenData.data || !tokenData.data.token) {
      console.error('❌ Etax token авахад алдаа:', tokenData.message);
      setErrorMessage('Etax token авахад алдаа: ' + (tokenData.message || 'Unknown error'));
      setShowErrorPopup(true);
      return { success: false, message: 'Etax token авахад алдаа' };
    }
    
    const token = tokenData.data.token;
    console.log('✅ Etax token амжилттай авлаа');
    
    // NeKey авах - localStorage cache шалгах
    let neKey = null;
    
    // Эхлээд localStorage-аас шалгах
    const cachedNeKey = localStorage.getItem('taxNeKey');
    const cacheTime = localStorage.getItem('taxNeKeyTime');
    
    // 30 минут (1800000ms) дотор байвал cache-аас авах
    if (cachedNeKey && cacheTime) {
      const timeDiff = Date.now() - parseInt(cacheTime);
      if (timeDiff < 1800000) { // 30 минут
        console.log('✅ NeKey localStorage-аас авлаа (cache):', cachedNeKey);
        neKey = cachedNeKey;
      }
    }
    
    // Cache байхгүй эсвэл хуучин бол API-аас авах
    if (!neKey) {
      console.log('🔍 NeKey API-аас авч байна...');
      const neKeyResult = await api.get('/tax-ne-key');
      console.log('🔍 NeKey result:', neKeyResult);
      
      if (!neKeyResult.success || !neKeyResult.data) {
        console.error('❌ NeKey авахад алдаа:', neKeyResult.message);
        setErrorMessage('NeKey авахад алдаа: ' + (neKeyResult.message || 'Unknown error'));
        setShowErrorPopup(true);
        return { success: false, message: 'NeKey авахад алдаа' };
      }
      
      neKey = neKeyResult.data;
      console.log('✅ NeKey API-аас авлаа:', neKey);
      
      // localStorage-д хадгалах
      localStorage.setItem('taxNeKey', neKey);
      localStorage.setItem('taxNeKeyTime', Date.now().toString());
      console.log('✅ NeKey localStorage-д хадгалагдлаа');
    }
    
    // Backend proxy-д дамжуулах мэдээлэл
    const requestData = {
      token: token,
      neKey: neKey,
      username: taxUsername,
      customerPin: customerPin,
      isIndividualTaxpayer: isIndividualTaxpayer // Add flag to help backend determine the correct API call
    };
    
    console.log('🔍 Backend proxy-д дамжуулах мэдээлэл:', {
      ...requestData,
      token: token ? '***' : 'NO TOKEN'
    });
    
    // Backend proxy-д etax.mta.mn API дуудалтын мэдээлэл нэмэх
    console.log('🔍 Backend proxy should call etax.mta.mn with:');
    if (isIndividualTaxpayer) {
      console.log('🔍 - Individual taxpayer detected - should use getTaxpayerInfo API');
      console.log('🔍 - URL: https://etax.mta.mn/api/taxpayer/getTaxpayerInfo?entId=' + customerPin);
    } else {
      console.log('🔍 - Organization taxpayer - using getUserOrgs API');
      console.log('🔍 - URL: https://etax.mta.mn/api/beta/user/getUserOrgs');
    }
    console.log('🔍 - Method: GET');
    console.log('🔍 - Headers: Authorization: Bearer ' + (token ? '***' : 'NO TOKEN'));
    console.log('🔍 - Headers: neKey: ' + neKey);
    console.log('🔍 - Headers: username: ' + taxUsername);
    if (customerPin) {
      console.log('🔍 - Query param: customerPin: ' + customerPin);
    }
    
    // Backend proxy-д дуудалт хийх
    const response = await fetch(`${API_BASE_URL}/api/etax-proxy/getUserOrgs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    
    const result = await response.json();
    console.log('🔍 Backend proxy response:', result);
    
    if (!result.success) {
      console.error('❌ Backend proxy алдаа:', result.message);
      setErrorMessage('Etax service алдаа: ' + (result.message || 'Unknown error'));
      setShowErrorPopup(true);
      return { success: false, message: result.message || 'Etax service алдаа' };
    }
    
    const actualData = result.data;
    console.log('🔍 Actual data from Etax:', actualData);
    
    if (!actualData || actualData.length === 0) {
      console.log('⚠️ Etax-ээс өгөгдөл ирээгүй');
      setErrorMessage('Etax системд мэдээлэл олдсонгүй');
      setShowErrorPopup(true);
      return { success: false, message: 'Etax системд мэдээлэл олдсонгүй' };
    }
    
    // Backend-ээс ирсэн өгөгдөл шууд ашиглах (taxpayerBranchView field байхгүй)
    console.log('🔍 Backend-ээс ирсэн өгөгдөл шууд ашиглах...');
    console.log('🔍 actualData length:', actualData.length);
    console.log('🔍 actualData[0]:', actualData[0]);
    
    const userData = actualData.map(item => {
      // refEntTypeCode-ийн утгыг зөв авах
      let refEntTypeCode = 'Organization'; // Default утга
      
      if (item.refEntTypeCode) {
        refEntTypeCode = item.refEntTypeCode;
      } else if (item.RefEntTypeName) {
        // Хэрэв refEntTypeCode байхгүй бол RefEntTypeName-ээс авах
        if (item.RefEntTypeName === 'Хуулийн этгээд' || item.RefEntTypeName === 'Хувь хүн') {
          refEntTypeCode = 'Individual';
        } else if (item.RefEntTypeName === 'Байгууллага' || item.RefEntTypeName === 'Хуулийн этгээд') {
          refEntTypeCode = 'Organization';
        }
      }
      
      console.log('🔍 refEntTypeCode mapping:', {
        originalRefEntTypeCode: item.refEntTypeCode,
        originalRefEntTypeName: item.RefEntTypeName,
        mappedRefEntTypeCode: refEntTypeCode
      });
      
      return {
        pin: item.pin,
        entId: item.taxpayerBranchView?.entId || item.id || item.entId,
        branchId: item.taxpayerBranchView?.branchId || item.branchId, // branchId-г нэмэх
        tin: item.tin,
        refEntTypeCode: refEntTypeCode,
        legalName: item.legalName,
        // Нэмэлт мэдээлэл хадгалах
        taxpayerBranchView: item.taxpayerBranchView,
        id: item.id
      };
    });
    
    // Form автомат бөглөх
    if (userData && userData.length > 0) {
      console.log('🔧 Form автомат бөглөх...');
      console.log('🔧 Олдсон organization-ууд:', userData.length);
      
      // Тодорхой кодын мэдээлэл олох (хэрэв customerPin-тай тохирох бол)
      let selectedOrg = null;
      if (customerPin && customerPin.trim()) {
        console.log('🔍 Тодорхой кодын мэдээлэл хайж байна:', customerPin);
        selectedOrg = userData.find(org => org.pin === customerPin.trim());
        if (selectedOrg) {
          console.log('✅ Тодорхой кодын мэдээлэл олдлоо:', JSON.stringify(selectedOrg, null, 2));
          
          // entId-ийг зөв авах (хэд хэдэн байж болох газар)
          const entId = selectedOrg.entId || selectedOrg.id || selectedOrg.taxpayerBranchView?.entId;
          console.log('✅ Тодорхой кодын entId:', entId);
          console.log('🔍 entId хайж байна:', {
            'selectedOrg.entId': selectedOrg.entId,
            'selectedOrg.id': selectedOrg.id,
            'selectedOrg.taxpayerBranchView?.entId': selectedOrg.taxpayerBranchView?.entId,
            'found entId': entId
          });
          
          // Харилцагч олдсон үед тайлбар арилгах
          setCustomerNotFoundMessage('');
        } else {
          console.log('❌ Тодорхой кодын мэдээлэл олдсонгүй, эхний organization ашиглах');
          // Харилцагч олдохгүй үед тайлбар харуулах
          setCustomerNotFoundMessage('Та татварын системд бүртгэлгүй харилцагч тул дараа бүртгэлээ шалгаад дараа нь мэдээллээ шинэчилнэ үү');
        }
      }
      
      // Хэрэв тодорхой кодын мэдээлэл олдохгүй бол selectedOrg null байх
      if (!selectedOrg) {
        console.log('❌ Харилцагч олдсонгүй, selectedOrg null байна');
        return { success: false, message: 'Харилцагч олдсонгүй' };
      }
      
      // Check if we have the required fields for tax login auto-population
      let gdsrNumber = selectedOrg.gdsrNumber;
      let shareHolderName = selectedOrg.shareHolderName;
      
      console.log('🔍 Checking required fields for tax login auto-population:', {
        hasGdsrNumber: !!gdsrNumber,
        hasShareHolderName: !!shareHolderName,
        availableFields: Object.keys(selectedOrg)
      });
      
      // If missing required fields, try to extract from available data
      if (!gdsrNumber || !shareHolderName) {
        console.log('⚠️ Required fields missing, trying to extract from available data...');
        
        // For individual taxpayers, use PIN as gdsrNumber and legalName as shareHolderName
        if (isIndividualTaxpayer) {
          console.log('🔧 Individual taxpayer - using PIN as gdsrNumber and legalName as shareHolderName');
          gdsrNumber = selectedOrg.pin;
          shareHolderName = selectedOrg.legalName || selectedOrg.entityName;
          
          console.log('✅ Extracted gdsrNumber (from PIN):', gdsrNumber);
          console.log('✅ Extracted shareHolderName (from legalName):', shareHolderName);
        } else {
          // For organizations, try to find alternative fields
          console.log('🔧 Organization taxpayer - trying to find alternative fields...');
          
          if (!gdsrNumber && selectedOrg.pin) {
            gdsrNumber = selectedOrg.pin;
            console.log('✅ Using PIN as gdsrNumber:', gdsrNumber);
          }
          
          if (!shareHolderName && selectedOrg.legalName) {
            shareHolderName = selectedOrg.legalName;
            console.log('✅ Using legalName as shareHolderName:', shareHolderName);
          }
        }
      }
      
      // Form field-уудыг бөглөх
      setForm(prev => ({
        ...prev,
        CustomerID: selectedOrg.pin || prev.CustomerID, // PIN-г CustomerID болгон ашиглах
        CustomerName: selectedOrg.legalName || selectedOrg.entityName || prev.CustomerName, // Legal name-г CustomerName болгон ашиглах
        TinCode: selectedOrg.tin || prev.TinCode, // TIN-г TinCode болгон ашиглах           
      }));
      
      console.log('🔧 Form auto-filled with basic data, now calling handleCustomerSelect for tax login...');
      
      // Also try immediate tax login auto-population if we have matching data
      console.log('🔧 Attempting immediate tax login auto-population with extracted data...');
      if (gdsrNumber && shareHolderName && allTaxLogins.length > 0) {
        // Try to find matching tax login using extracted data with improved Cyrillic handling
        console.log('🔍 Searching for tax login match using extracted data:', {
          gdsrNumber,
          shareHolderName,
          originalPin: selectedOrg.pin,
          availableTaxLogins: allTaxLogins.map(tl => tl.TaxUsername)
        });
        
        const matchingTaxLogin = allTaxLogins.find(tl => {
          if (!tl.TaxUsername) return false;
          
          // Direct gdsrNumber match
          if (gdsrNumber && tl.TaxUsername.includes(gdsrNumber)) {
            console.log('✅ Direct gdsrNumber match:', tl.TaxUsername);
            return true;
          }
          
          // Direct PIN match (fallback)
          if (selectedOrg.pin && tl.TaxUsername.includes(selectedOrg.pin)) {
            console.log('✅ Direct PIN match:', tl.TaxUsername);
            return true;
          }
          
          // Match by extracting numeric parts using helper function
          const gdsrPart = extractNumericPart(gdsrNumber || selectedOrg.pin);
          const taxLoginPart = extractNumericPart(tl.TaxUsername);
          
          if (taxLoginPart.includes(gdsrPart) && gdsrPart.length > 3) {
            console.log('✅ Numeric gdsrNumber match:', tl.TaxUsername, 'with', gdsrPart);
            return true;
          }
          
          // Try matching the first few characters of the gdsrNumber
          if (gdsrPart.length >= 4) {
            const gdsrPrefix = gdsrPart.substring(0, 4);
            if (taxLoginPart.includes(gdsrPrefix)) {
              console.log('✅ gdsrNumber prefix match:', tl.TaxUsername, 'with', gdsrPrefix);
              return true;
            }
          }
          
          return false;
        });
        
        if (matchingTaxLogin) {
          console.log('✅ Found matching tax login with extracted data:', matchingTaxLogin);
          setForm(prev => ({
            ...prev,
            TaxLoginOid: matchingTaxLogin.Oid,
            // Also populate director fields with extracted data
            DirectorRegister: gdsrNumber || prev.DirectorRegister,
            DrFirstname: shareHolderName ? shareHolderName.split(' ')[0] : prev.DrFirstname,
            DrLastname: shareHolderName ? shareHolderName.split(' ').slice(1).join(' ') : prev.DrLastname,
            // Auto-populate UserName with DirectorRegister
            UserName: gdsrNumber || prev.DirectorRegister || prev.UserName
          }));
          console.log('✅ Tax login auto-selected with extracted data:', matchingTaxLogin.TaxUsername);
          console.log('✅ Director fields auto-populated with extracted data:', {
            gdsrNumber,
            shareHolderName,
            userName: gdsrNumber
          });
        } else {
          console.log('⚠️ No matching tax login found with extracted data');
          console.log('⚠️ Available tax logins:', allTaxLogins.map(tl => tl.TaxUsername));
          
          // Try using the autoPopulateTaxLogin function as fallback
          if (gdsrNumber && shareHolderName) {
            console.log('🔧 Trying fallback auto-population with extracted data...');
            console.log('🔧 Fallback data:', {
              gdsrNumber,
              shareHolderName
            });
            autoPopulateTaxLogin(gdsrNumber, shareHolderName, allTaxLogins, setForm);
          }
        }
      } else {
        console.log('⚠️ allTaxLogins is empty, cannot auto-populate tax login');
        console.log('⚠️ allTaxLogins length:', allTaxLogins.length);
        
        // Try using the autoPopulateTaxLogin function as fallback even without allTaxLogins
        if (gdsrNumber && shareHolderName) {
          console.log('🔧 Trying fallback auto-population with extracted data (no allTaxLogins)...');
          console.log('🔧 Fallback data (no allTaxLogins):', {
            gdsrNumber,
            shareHolderName
          });
          autoPopulateTaxLogin(gdsrNumber, shareHolderName, allTaxLogins, setForm);
        }
      }
      
      console.log('✅ Form автомат бөглөгдлөө');
      console.log('🔧 CustomerID (PIN):', selectedOrg.pin);
      console.log('🔧 CustomerName (Legal Name):', selectedOrg.legalName || selectedOrg.entityName); 
      console.log('🔧 TinCode (TIN):', selectedOrg.tin);
      console.log('🔧 Extracted gdsrNumber:', gdsrNumber);
      console.log('🔧 Extracted shareHolderName:', shareHolderName);
      
      // gdsrNumber авах - хувь хүний хувьд алгасах
      if (isIndividualTaxpayer) {
        console.log('🔧 Хувь хүн байна - gdsrNumber авах процесс алгаслаа');
        console.log('🔧 PIN болон legalName-ээс мэдээлэл авсан, нэмэлт API дуудлага хийхгүй');
        
        // Хувь хүний хувьд шууд database-д хадгалах
        console.log('🔧 Хувь хүний мэдээллийг database-д хадгалах...');
        try {
          await saveCustomerToDatabase(selectedOrg);
          console.log('✅ Хувь хүний мэдээлэл database-д хадгалагдлаа');
        } catch (error) {
          console.error('❌ Хувь хүний мэдээлэл database-д хадгалахад алдаа:', error);
        }
      } else {
        console.log('🔧 Байгууллага байна - gdsrNumber авах процесс эхэлж байна...');
        await handleCustomerSelect(selectedOrg);
      }
      
      // Try immediate tax login auto-population if tax logins are already loaded
      console.log('🔧 Checking if tax logins are available for immediate auto-population...');
      if (allTaxLogins.length > 0) {
        console.log('🔧 Tax logins available, attempting immediate auto-population...');
        // Try to find matching tax login using extracted data with improved Cyrillic handling
        console.log('🔍 Searching for tax login match using extracted data:', {
          gdsrNumber,
          shareHolderName,
          originalPin: selectedOrg.pin,
          availableTaxLogins: allTaxLogins.map(tl => tl.TaxUsername)
        });
        
        const matchingTaxLogin = allTaxLogins.find(tl => {
          if (!tl.TaxUsername) return false;
          
          // Direct gdsrNumber match
          if (gdsrNumber && tl.TaxUsername.includes(gdsrNumber)) {
            console.log('✅ Direct gdsrNumber match:', tl.TaxUsername);
            return true;
          }
          
          // Direct PIN match (fallback)
          if (selectedOrg.pin && tl.TaxUsername.includes(selectedOrg.pin)) {
            console.log('✅ Direct PIN match:', tl.TaxUsername);
            return true;
          }
          
          // Match by extracting numeric parts using helper function
          const gdsrPart = extractNumericPart(gdsrNumber || selectedOrg.pin);
          const taxLoginPart = extractNumericPart(tl.TaxUsername);
          
          if (taxLoginPart.includes(gdsrPart) && gdsrPart.length > 3) {
            console.log('✅ Numeric gdsrNumber match:', tl.TaxUsername, 'with', gdsrPart);
            return true;
          }
          
          // Try matching the first few characters of the gdsrNumber
          if (gdsrPart.length >= 4) {
            const gdsrPrefix = gdsrPart.substring(0, 4);
            if (taxLoginPart.includes(gdsrPrefix)) {
              console.log('✅ gdsrNumber prefix match:', tl.TaxUsername, 'with', gdsrPrefix);
              return true;
            }
          }
          
          return false;
        });
        
        if (matchingTaxLogin) {
          console.log('✅ Found matching tax login with extracted data:', matchingTaxLogin);
          setForm(prev => ({
            ...prev,
            TaxLoginOid: matchingTaxLogin.Oid,
            // Also populate director fields with extracted data
            DirectorRegister: gdsrNumber || prev.DirectorRegister,
            DrFirstname: shareHolderName ? shareHolderName.split(' ')[0] : prev.DrFirstname,
            DrLastname: shareHolderName ? shareHolderName.split(' ').slice(1).join(' ') : prev.DrLastname,
            // Auto-populate UserName with DirectorRegister
            UserName: gdsrNumber || prev.DirectorRegister || prev.UserName
          }));
          console.log('✅ Tax login auto-selected with extracted data:', matchingTaxLogin.TaxUsername);
          console.log('✅ Director fields auto-populated with extracted data:', {
            gdsrNumber,
            shareHolderName,
            userName: gdsrNumber
          });
        } else {
          console.log('⚠️ No matching tax login found with extracted data');
          console.log('⚠️ Available tax logins:', allTaxLogins.map(tl => tl.TaxUsername));
          
          // Try using the autoPopulateTaxLogin function as fallback
          if (gdsrNumber && shareHolderName) {
            console.log('🔧 Trying fallback auto-population with extracted data...');
            console.log('🔧 Fallback data:', {
              gdsrNumber,
              shareHolderName
            });
            autoPopulateTaxLogin(gdsrNumber, shareHolderName, allTaxLogins, setForm);
          }
        }
      }
      
      return { success: true, data: actualData };
    } else {
      console.log('⚠️ userData хоосон байна');
      setErrorMessage('Etax системд мэдээлэл олдсонгүй');
      setShowErrorPopup(true);
      return { success: false, message: 'Etax системд мэдээлэл олдсонгүй' };
    }
  } catch (error: any) {
    console.error('❌ getUserOrgsData алдаа:', error);
    setErrorMessage(error.message || 'Etax service-ээс мэдээлэл авахад алдаа гарлаа');
    setShowErrorPopup(true);
    return { success: false, message: error.message };
  }
};

// Import necessary functions
import { handleCustomerSelect, saveCustomerToDatabase } from './customerWorkflow';
